import * as React from 'react';

import AppState from '@webapp/store/state';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { UIMenus } from '@webapp/store/ui/types';
import { OpenMenu, CloseMenu } from '@webapp/store/ui/action-creators';

import styled, { css } from 'styled-components';

import { AnimatePresence, motion } from 'framer-motion';

import Button from '@components/actions/button';
import CloseIcon from '@components/basics/icons/close';
import { Translate } from 'react-localize-redux';
import Typography from '@components/basics/typography';

const easing = [0.0, 0.005, 0.2, 1.0];

const desktop = {
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.3,
      ...easing,
    },
  },
  hidden: {
    opacity: 0,
    x: '100%',
  },
  exit: {
    opacity: 0,
    x: '100%',
    transition: {
      duration: 0.6,
      ...easing,
    },
  },
};

const mobile = {
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ...easing,
    },
  },
  hidden: {
    opacity: 0,
    y: '100%',
  },
  exit: {
    opacity: 0,
    y: '100%',
    transition: {
      duration: 0.6,
      ...easing,
    },
  },
};

interface NewMenuProps {
  className?: string;
  children?: any;
  title?: string;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  button?: React.ReactNode;
  topHeader?: React.ReactNode;
  closeButton?: React.ReactNode;
  noButton?: boolean;
  menuOpened?: UIMenus;
  menuId: UIMenus;
  isMobile?: boolean;
  normalPadding?: boolean;
  centeredPadding?: boolean;
  forcedMenu?: string;
  openMenu: (menuId: UIMenus) => OpenMenu;
  closeMenu: () => CloseMenu;
  onClose?: () => void;
  onMenuOpened?: () => void;
}

const mapStateToProps = (state: AppState) => ({
  menuOpened: state.ui.menuOpened,
  isMobile: state.ui.mobile,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  openMenu: (menuId: UIMenus) => dispatch(new OpenMenu(menuId)),
  closeMenu: () => dispatch(new CloseMenu(undefined, true)),
});

export const NewMenu: React.FC<NewMenuProps> = (props) => {
  const isOpened = props.menuOpened === props.menuId;

  const ButtonOpen = props.button ? (
    props.button
  ) : props.noButton ? null : (
    <Button>
      <Typography>
        <Translate id={props.title} />
      </Typography>
    </Button>
  );

  const handleClick = () => {
    if (isOpened) {
      if (props.onClose) {
        props.onClose();
      } else {
        props.closeMenu();
      }
    } else {
      if (props.forcedMenu) {
        props.openMenu(props.forcedMenu);
        return;
      }
      props.openMenu(props.menuId);

      if (props?.onMenuOpened) {
        props.onMenuOpened();
      }
    }
  };

  const appHeight = () => {
    return `${window.innerHeight}px`;
  };

  return (
    <NewMenuContainer
      topHeader={props.topHeader}
      header={props.header}
      footer={props.footer}
      className={props.className}
      normalPadding={props.normalPadding}
      centeredPadding={props.centeredPadding}
      isMobile={props.isMobile}
    >
      <div className="menu-action" onClick={handleClick}>
        {ButtonOpen}
      </div>
      <AnimatePresence exitBeforeEnter>
        {isOpened && (
          <>
            {!props.isMobile && (
              <motion.div
                className="close-wrapper"
                initial="hidden"
                animate={isOpened ? 'visible' : 'hidden'}
                exit="exit"
                variants={props.isMobile ? mobile : desktop}
              >
                <CloseIcon
                  className="close-icon"
                  onClick={props.onClose ? props.onClose : props.closeMenu}
                />
              </motion.div>
            )}
            <motion.div
              className={`new-menu-items-container ${isOpened ? 'opened' : ''}`}
              initial="hidden"
              animate={isOpened ? 'visible' : 'hidden'}
              exit="exit"
              variants={props.isMobile ? mobile : desktop}
              style={{ height: props.isMobile ? appHeight() : undefined }}
            >
              {props.isMobile && (
                <motion.div
                  className={`close-wrapper flex ${
                    !props.topHeader ? 'flex-end' : ''
                  }`}
                  initial="hidden"
                  animate={isOpened ? 'visible' : 'hidden'}
                  exit="exit"
                  variants={props.isMobile ? mobile : desktop}
                >
                  {props.topHeader}
                  <CloseIcon
                    className="close-icon"
                    onClick={props.onClose ? props.onClose : props.closeMenu}
                  />
                </motion.div>
              )}
              {props.header && (
                <div className="menu-header">{props.header}</div>
              )}
              <div className="menu-content">{props.children}</div>
              {props.footer && (
                <div className="menu-footer">{props.footer}</div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </NewMenuContainer>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(NewMenu);

const NewMenuContainer = styled.div<{
  topHeader?: React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  normalPadding?: boolean;
  centeredPadding?: boolean;
  isMobile?: boolean;
}>`
  --menu-width: 500px;
  --menu-content-padding: 24px;

  .menu-action {
    flex-grow: 1;
    z-index: 9999;
  }

  .close-wrapper {
    cursor: pointer;
    margin: 0 auto 20px;

    ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
      position: fixed;
      top: 36px;
      right: calc(var(--menu-width) + 18px);
      bottom: auto;
      left: auto;
      width: 24px;
      height: 24px;
      margin: 0;
      z-index: 9999;
    }

    ${(props) =>
      props.isMobile
        ? css`
            position: sticky;
            top: 0;
            background-color: ${({ theme }) =>
              theme.colors.differentBackground};
            z-index: 9999;
            padding: 24px 8px;
          `
        : null}

    .close-icon {
      width: 32px;
      height: 32px;
      ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
        width: 24px;
        height: 24px;
      }
    }

    &.flex {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;

      &.flex-end {
        justify-content: flex-end;
      }
    }
  }

  .new-menu-items-container {
    background-color: ${({ theme }) => theme.colors.differentBackground};
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    pointer-events: none;
    box-sizing: border-box;
    width: 100%;
    width: 100vw;
    height: 100%;
    height: 100vh;
    overflow-y: scroll;
    border-radius: 24px 24px 0 0;

    .menu-header {
      padding: 24px;
      max-height: 120px;
      height: auto;
    }

    .menu-content {
      height: ${(props) =>
        props.topHeader ? css`fit-content` : css`var(--app-height)`};
      ${(props) =>
        props.topHeader || props.header || props.footer
          ? css`
              overflow-y: scroll;
            `
          : null}
    }

    .menu-footer {
      padding: 24px;
      height: 108px;
      justify-content: center;
      background-color: ${({ theme }) => theme.colors.differentBackground};
      z-index: 9999;
      position: relative;

      ${(props) =>
        props.isMobile
          ? css`
              position: sticky;
              bottom: 0;
            `
          : null}

      button {
        flex-grow: 1;
      }
    }

    ${(props) =>
      props.header
        ? css`
            .menu-content {
              height: calc(
                var(--app-height) - (var(--menu-content-padding) * 2) - 120px
              );
            }
          `
        : null}

    ${(props) =>
      props.footer
        ? css`
            .menu-content {
              height: calc(
                var(--app-height) - (var(--menu-content-padding) * 2) - 108px
              );
            }
          `
        : null}

    ${(props) =>
      props.header && props.footer
        ? css`
            .menu-content {
              height: calc(
                var(--app-height) - (var(--menu-content-padding) * 2) - 120px -
                  108px
              );
            }
          `
        : null}

    ${(props) =>
      props.topHeader && props.footer
        ? css`
            .menu-content {
              height: calc(
                var(--app-height) - (var(--menu-content-padding) * 2) - 32px -
                  108px
              );
            }
          `
        : null}
    ${(props) =>
      props.topHeader && props.header && props.footer
        ? css`
            .menu-content {
              height: calc(
                var(--app-height) - (var(--menu-content-padding) * 2) - 32px -
                  120px - 108px
              );
            }
          `
        : null}

    @media ${({ theme }) => theme.device.tablet} {
      border-radius: 0;
      top: 0;
      right: 0;
      bottom: 0;
      left: auto;
      width: var(--menu-width);
      height: 100vh;

      .menu-header {
        max-height: 160px;
        min-height: 50px;
        height: auto;
        padding: 30px 72px 20px 56px;
      }

      .menu-content {
        padding: 24px 54px 24px 54px;
        overflow-y: auto;

        ${(props) =>
          props.header && props.footer
            ? css`
                height: calc(100vh - 140px - 20px);
              `
            : (props.header && !props.footer) || (!props.header && props.footer)
            ? css`
                height: fit-content;
              `
            : css`
                height: 100%;
              `}
      }

      .menu-footer {
        padding: 10px 72px 40px 56px;
        height: 60px;
        justify-content: center;
      }
    }

    ${(props) =>
      props.normalPadding
        ? css`
            padding: 24px;

            ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
              padding: 32px 0 0 56px;

              .menu-content {
                padding: 0;
                max-height: none;
              }
            }
          `
        : props.centeredPadding
        ? css`
            .menu-content {
              padding: 24px 48px 24px 54px;
            }
          `
        : css`
            .menu-content {
              padding: var(--menu-content-padding);
            }

            ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
              padding: inherit;
            }

            .close-wrapper {
              margin: 24px;
            }
          `}

    &.opened {
      pointer-events: initial;
      z-index: 9999;
    }
  }
`;
